/*
 * Adapptio Editor
 *
 * @package editor-ui
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useContext, useMemo } from "react";
import { BEM, IIconPackageURLMapping, MediaContext } from "@adapptio/lib-components";
import { SvgImage } from "../SvgImage";

import "./Icon.scss";

export enum ICON_SIZE {
	XSMALL = "xsmall", // 16
	SMALL = "small", // 20
	MEDIUM = "medium", // 24
	LARGE = "large", // 32
	CUSTOM = "custom"
}

export interface IIconProps {
	name: string;
	size?: ICON_SIZE;
	className?: string;
	showLoader?: boolean;
	/* Overrides url mapping from media context */
	urlMapping?: IIconPackageURLMapping;
	style?: React.CSSProperties;
}

const Icon: React.FC<IIconProps> = ({ className, name, size, showLoader, urlMapping, style }) => {
	const [ packageName, iconName ] = (name || "/").split("/");
	const mediaContext = useContext(MediaContext);

	if (!packageName || !iconName || !(mediaContext || urlMapping)) {
		return null;
	}

	const prefixUrl = urlMapping ? urlMapping[packageName] : mediaContext?.iconPackageUrlMapping[packageName];
	const path = `${prefixUrl}/${iconName}.svg`;

	return useMemo(() => {
		const baseClass = BEM("edt-icon");
		baseClass.mod("size-" + size);

		if (className) {
			baseClass.add(className);
		}

		return (
			<span className={baseClass.toClassName()} style={style}>
				<SvgImage source={path} showLoader={showLoader} cacheRequests={true} />
			</span>
		);
	}, [ path, showLoader, size, className ]);
};

Icon.defaultProps = {
	size: ICON_SIZE.CUSTOM
};

const IconMemo = React.memo(Icon);

export { IconMemo as Icon };
